import * as Dialog from "@radix-ui/react-dialog";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Check, Copy, MessageCircle, Share2, X, ImageDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils/cn";
import { restoreFocus, useFocusRestoreTarget } from "@/lib/a11y/focusRestore";

export interface SharePayload {
  /** Dialog title (e.g. "分享「邀請大加入」"). */
  title: string;
  /** Body text shared to the target app (truncate upstream if needed). */
  text: string;
  /** Canonical URL (universal link target). Required. */
  url: string;
  /**
   * Optional files array (Blob[]) for photo shares (e.g. diary hero image).
   * Per Web Share API spec, the `files` field only works in browsers that
   * support file sharing (Chrome 89+, Safari 14+).  Falls back to URL-only
   * share on platforms that don't support files.
   */
  files?: readonly File[];
  /** Optional expiry hint shown in the gold info-banner (e.g. "7 天後過期"). */
  expiresLabel?: string;
  /** Optional callback after a successful native share (cancel NOT counted). */
  onShared?: (method: "native" | "copy" | "message") => void;
  /** Optional callback when the user dismisses without sharing. */
  onDismissed?: () => void;
}

export interface ShareSheetProps extends SharePayload {
  /** Controls the sheet visibility. */
  open: boolean;
  /** Close handler (required when `open` is controlled). */
  onClose: () => void;
  /** Optional testId for E2E / screenshot automation. */
  testId?: string;
}

/**
 * ShareSheet — content-aware share surface.
 *
 * Per `docs/spec/w2-frontend-components.md §7`:
 *   - Tries `navigator.share()` (Web Share API) FIRST when available.
 *   - Falls back to in-app share sheet with zh-Hant row labels when:
 *       (a) `'share' in navigator === false`
 *       (b) `navigator.share()` rejects with AbortError (user cancelled native)
 *       (c) Any other error from native share path
 *   - Always offers "複製連結" (clipboard) as a guaranteed fallback.
 *   - When `files` is provided, shows "儲存圖片到相簿" using a temporary
 *     anchor download (best-effort cross-browser; native share uses files[]).
 *   - Uses BottomSheet-style full-screen overlay (per mockup mobile.html §8.3).
 *   - Closes on backdrop tap / Esc / close button.
 *
 * Renders nothing when `open === false`.
 *
 * Anti-pattern ban (§7.5):
 *   - NO WeChat / Instagram SDK (heavyweight, brittle).
 *   - NO custom share UI that mimics iOS share sheet icons.
 *   - Uses native `navigator.share()` + canonical URL only.
 *
 * **W56.0 E — built on Radix `Dialog`.** It used to declare
 * `role="dialog" aria-modal="true"` by hand, with no focus trap, no focus
 * restore and no inert background behind it. Radix supplies all three, and
 * the Escape / backdrop dismissal now arrives through Radix's dismissable
 * layer instead of a hand-written `window` keydown listener — which also
 * removes the double-close that two independent Escape handlers would have
 * produced in a real browser (a keydown on an element bubbles to `window`;
 * Radix listens on `document`).
 */
export function ShareSheet({
  open,
  onClose,
  title,
  text,
  url,
  files,
  expiresLabel,
  onShared,
  onDismissed,
  testId,
}: ShareSheetProps) {
  // W56.0 E — the element that had focus before the sheet opened.
  const restoreTarget = useFocusRestoreTarget(open);
  const [nativeShareAttempted, setNativeShareAttempted] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  // Reset copy state whenever the sheet re-opens.
  useEffect(() => {
    if (open) setCopyState("idle");
  }, [open]);

  const webShareSupported =
    typeof navigator !== "undefined" && typeof (navigator as Navigator).share === "function";

  const tryNativeShare = useCallback(async () => {
    if (!webShareSupported) return false;
    try {
      // Web Share API: files is optional but only works on supporting browsers.
      const data: ShareData = { title, text, url };
      if (files && files.length > 0 && typeof (navigator as Navigator).canShare === "function") {
        const canShareFiles = (navigator as Navigator).canShare?.({
          files: files as unknown as File[],
        });
        if (canShareFiles) {
          data.files = files as unknown as File[];
        }
      }
      await (navigator as Navigator).share(data);
      onShared?.("native");
      onClose();
      return true;
    } catch (err) {
      // AbortError means user cancelled native sheet — keep our fallback open.
      const isAbort =
        err instanceof Error &&
        (err.name === "AbortError" || err.message.toLowerCase().includes("abort"));
      if (isAbort) return true; // don't fall through to fallback close
      // Real error (NotAllowedError, TypeError on unsupported types, etc.) → fall through
      return false;
    }
  }, [webShareSupported, title, text, url, files, onShared, onClose]);

  // Auto-attempt native share on first open when supported (per HIG: prefer native).
  useEffect(() => {
    if (open && webShareSupported && !nativeShareAttempted) {
      setNativeShareAttempted(true);
      void tryNativeShare().then((success) => {
        if (!success) {
          // Fallback already rendered; user can pick a row.
        }
      });
    }
    if (!open) setNativeShareAttempted(false);
  }, [open, webShareSupported, nativeShareAttempted, tryNativeShare]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState("copied");
      onShared?.("copy");
    } catch {
      setCopyState("failed");
    }
  }, [url, onShared]);

  const handleMessageShare = useCallback(async () => {
    // "訊息" = chat-style share. Try native share first (most phones route
    // to Messages app via UIActivityViewController); if unavailable or fails,
    // fall back to copy + toast.
    if (webShareSupported) {
      const ok = await tryNativeShare();
      if (ok) {
        onShared?.("message");
        return;
      }
    }
    await handleCopy();
    onShared?.("message");
  }, [webShareSupported, tryNativeShare, handleCopy, onShared]);

  const handleSaveImage = useCallback(() => {
    if (!files || files.length === 0) return;
    // Best-effort: download each file via a temporary anchor.
    for (const file of files) {
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name || "shared-image";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Defer revoke so the browser can actually start the download.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }, [files]);

  const handleClose = useCallback(() => {
    onDismissed?.();
    onClose();
  }, [onDismissed, onClose]);

  if (!open) return null;

  const copyLabel =
    copyState === "copied" ? "已複製連結" : copyState === "failed" ? "複製失敗" : "複製連結";

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        // W56.0 E — Radix's dismissable layer handles Escape and the backdrop
        // tap. Both count as 「dismissed without sharing」, exactly as the
        // hand-written keydown listener did.
        if (!next) {
          onDismissed?.();
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <div
          data-component="share-sheet"
          data-testid={testId ?? "share-sheet"}
          data-open="true"
          className="fixed inset-0 z-50"
        >
          <Dialog.Overlay
            data-testid="share-sheet-backdrop"
            className="absolute inset-0 bg-ink-1/40 backdrop-blur-sm animate-share-sheet-backdrop"
          />
          <Dialog.Content
            data-testid="share-sheet-panel"
            // Radix 1.1 renders `role="dialog"` on `Dialog.Content` but **not**
            // `aria-modal` — the attribute was dropped upstream — so the
            // modal claim is stated here explicitly.
            aria-modal="true"
            // W56.0 E — give the focus back to whatever opened the sheet.
            onCloseAutoFocus={(event) => restoreFocus(event, restoreTarget.current)}
            className={cn(
              "absolute inset-x-0 bottom-0 max-h-[92vh]",
              "bg-paper-elevated rounded-t-3xl shadow-modal",
              "flex flex-col",
              "animate-share-sheet-panel",
              "pb-[max(env(safe-area-inset-bottom),12px)]",
            )}
          >
            <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-hairline-strong" />
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <Dialog.Title className="font-display text-title-2 tracking-tight text-ink-1">
                分享
              </Dialog.Title>
              <button
                type="button"
                onClick={handleClose}
                aria-label="關閉"
                data-testid="share-sheet-close"
                className="press inline-flex h-11 w-11 items-center justify-center rounded-full text-ink-2 hover:bg-black/5 transition-colors duration-quick ease-paper"
              >
                <X size={20} aria-hidden />
              </button>
            </div>
            <div className="text-center text-body-sm text-ink-3 px-5 pb-3 truncate">{title}</div>

            <div className="overflow-y-auto flex-1 px-2 pb-4">
              <ul
                className="bg-paper-elevated rounded-2xl overflow-hidden divide-y divide-hairline"
                data-testid="share-sheet-rows"
              >
                <ShareRow
                  icon={<MessageCircle size={18} aria-hidden />}
                  iconBgClass="bg-[rgb(34_197_94)]"
                  iconTextClass="text-paper"
                  label="訊息"
                  onClick={handleMessageShare}
                  testId="share-sheet-row-message"
                />
                <ShareRow
                  icon={<Copy size={18} aria-hidden />}
                  iconBgClass=""
                  iconTextClass="text-you-strong"
                  label={copyLabel}
                  onClick={handleCopy}
                  testId="share-sheet-row-copy"
                  trailing={
                    copyState === "copied" ? (
                      <Check
                        size={16}
                        aria-hidden
                        className="text-you-strong"
                      />
                    ) : null
                  }
                />
                {files && files.length > 0 ? (
                  <ShareRow
                    icon={<ImageDown size={18} aria-hidden />}
                    iconBgClass=""
                    iconTextClass="text-you-strong"
                    label="儲存圖片到相簿"
                    onClick={handleSaveImage}
                    testId="share-sheet-row-save-image"
                  />
                ) : null}
              </ul>

              {expiresLabel ? (
                <div
                  data-testid="share-sheet-expires"
                  className="mt-4 mx-3 flex items-start gap-2 rounded-md border-l-4 border-gold bg-gold/5 px-3 py-2"
                >
                  <Share2 size={16} className="shrink-0 text-gold-ink mt-0.5" aria-hidden />
                  <div>
                    <div className="text-meta font-semibold text-ink-1">邀請碼有效期限</div>
                    <div className="text-body-sm text-ink-2 mt-1">{expiresLabel}</div>
                  </div>
                </div>
              ) : null}

              <div className="mt-4 px-3">
                <Button variant="ghost" size="md" onClick={handleClose} className="w-full">
                  取消
                </Button>
              </div>
            </div>
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface ShareRowProps {
  icon: ReactNode;
  iconBgClass: string;
  iconTextClass: string;
  label: string;
  onClick: () => void;
  testId?: string;
  trailing?: ReactNode;
}

function ShareRow({
  icon,
  iconBgClass,
  iconTextClass,
  label,
  onClick,
  testId,
  trailing,
}: ShareRowProps) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className={cn(
          "press flex w-full items-center gap-3 px-4 py-3.5 text-left",
          "hover:bg-black/5 transition-colors duration-quick ease-paper",
        )}
      >
        <span
          className={cn(
            "inline-flex h-7 w-7 items-center justify-center rounded-full shrink-0",
            iconBgClass,
            iconTextClass,
          )}
          aria-hidden
        >
          {icon}
        </span>
        <span className="flex-1 text-body text-ink-1 truncate">{label}</span>
        {trailing}
        <span className="text-ink-3 text-title-3" aria-hidden>
          ›
        </span>
      </button>
    </li>
  );
}
